'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { 
  Plus, Disc3, Radio, Send, TrendingUp, Wallet, 
  Sparkles, Globe2, ArrowRight, RefreshCw, Music2, ShieldCheck 
} from 'lucide-react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

export default function ArtistDashboardPage() {
  const supabase = createClient();
  const [loading, setLoading] = useState(true);
  const [releases, setReleases] = useState<any[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userPlan, setUserPlan] = useState('free');

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      const { data: sub } = await supabase
        .from('subscriptions')
        .select('plan, status')
        .eq('user_id', user.id)
        .maybeSingle();

      const { data: userReleases } = await supabase
        .from('releases')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      setUserProfile(profile);
      setUserPlan(sub?.plan || profile?.plan || 'free');
      setReleases(userReleases || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const totalReleases = releases.length;
  const liveReleases = releases.filter(r => (r.status || '').toUpperCase() === 'LIVE').length;
  const deliveredReleases = releases.filter(r => ['DELIVERED', 'QC_PENDING', 'APPROVED'].includes((r.status || '').toUpperCase())).length;

  const artistName = userProfile?.artist_name || userProfile?.full_name || 'Artist';

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-20 font-sans">
        
        {/* Premium Glassmorphism Welcome Banner */}
        <div className="relative p-7 rounded-3xl bg-gradient-to-r from-[#111422] via-[#161a2b] to-[#111422] border border-zinc-800/80 shadow-2xl overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-extrabold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                <Sparkles size={11} /> Verified Artist Portal
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                Welcome back, {artistName} 👋
              </h1>
              <p className="text-xs text-zinc-400">
                Your global music distribution pipeline is active across 150+ streaming networks.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadDashboardData}
                className="p-3 rounded-2xl bg-[#0c0e18] border border-zinc-800 text-zinc-400 hover:text-white transition shadow-inner cursor-pointer"
                title="Refresh Metrics"
              >
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
              <Link
                href="/create-release"
                className="flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-xs text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-xl shadow-purple-600/30 transition transform active:scale-95"
              >
                <Plus size={16} /> New Release
              </Link>
            </div>
          </div>
        </div>

        {/* 3 High-Tech Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Card 1: Total Streams */}
          <div className="p-6 rounded-3xl bg-[#111422] border border-zinc-800 shadow-xl space-y-3 relative overflow-hidden group hover:border-purple-500/50 transition">
            <div className="flex items-center justify-between">
              <span className="text-2xs font-bold uppercase tracking-wider text-zinc-400">Total Streams (30D)</span>
              <div className="w-9 h-9 rounded-2xl bg-purple-600/10 text-purple-400 flex items-center justify-center">
                <TrendingUp size={18} />
              </div>
            </div>
            <div className="text-3xl font-black text-white">0</div>
            <div className="text-2xs text-zinc-500 flex items-center gap-1">
              <Globe2 size={12} className="text-purple-400" /> Auto-syncs across Spotify & Apple Music
            </div>
          </div>

          {/* Card 2: Estimated Royalties */}
          <div className="p-6 rounded-3xl bg-[#111422] border border-zinc-800 shadow-xl space-y-3 relative overflow-hidden group hover:border-emerald-500/50 transition">
            <div className="flex items-center justify-between">
              <span className="text-2xs font-bold uppercase tracking-wider text-emerald-400">Estimated Royalties</span>
              <div className="w-9 h-9 rounded-2xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
                <Wallet size={18} />
              </div>
            </div>
            <div className="text-3xl font-black text-white">₹0.00</div>
            <div className="text-2xs text-emerald-400/80 flex items-center gap-1 font-medium">
              <span>Ready for payout (Min: ₹2,500)</span>
            </div>
          </div>

          {/* Card 3: Releases in Pipeline */}
          <div className="p-6 rounded-3xl bg-[#111422] border border-zinc-800 shadow-xl space-y-3 relative overflow-hidden group hover:border-blue-500/50 transition">
            <div className="flex items-center justify-between">
              <span className="text-2xs font-bold uppercase tracking-wider text-blue-400">Catalog & Pipeline</span>
              <div className="w-9 h-9 rounded-2xl bg-blue-500/10 text-blue-400 flex items-center justify-center">
                <Disc3 size={18} />
              </div>
            </div>
            <div className="text-3xl font-black text-white">{totalReleases}</div>
            <div className="text-2xs text-blue-400/80 flex items-center gap-1 font-medium">
              <span>{liveReleases} Live • {deliveredReleases} Delivered</span>
            </div>
          </div>
        </div>

        {/* Your Releases Section */}
        <div className="p-7 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-white">Your Releases</h2>
              <p className="text-xs text-zinc-400 mt-0.5">Manage tracks, review QC reports, and inspect metadata.</p>
            </div>
            <Link
              href="/releases"
              className="text-xs font-bold text-purple-400 hover:text-purple-300 flex items-center gap-1 transition"
            >
              View All Catalog <ArrowRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="py-20 text-center text-xs text-zinc-500">Loading your catalog...</div>
          ) : releases.length === 0 ? (
            <div className="py-16 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto text-zinc-600">
                <Music2 size={32} />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-bold text-white">No Releases in Catalog</h3>
                <p className="text-xs text-zinc-400 max-w-sm mx-auto">
                  You currently have 0 releases. Click below to distribute your first song worldwide.
                </p>
              </div>
              <Link
                href="/create-release"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/30 transition"
              >
                <Plus size={15} /> Create Release
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {releases.slice(0, 3).map((rel) => (
                <div key={rel.id} className="p-4 rounded-2xl bg-[#161a2b] border border-zinc-800/80 space-y-3 hover:border-purple-500/50 transition">
                  <div className="flex items-center gap-3">
                    <img
                      src={rel.artwork_url || rel.cover_url || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=300'}
                      alt={rel.title}
                      className="w-12 h-12 rounded-xl object-cover border border-zinc-800"
                    />
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-white truncate">{rel.title}</h4>
                      <p className="text-2xs text-purple-400 font-medium truncate">{rel.primary_artist}</p>
                      <p className="text-[10px] text-zinc-500">{rel.label || 'Tubenexa Distribution'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-800 text-2xs">
                    <span className="px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      {rel.status || 'Delivered'}
                    </span>
                    <span className="text-zinc-400 font-mono">{rel.upc || 'UPC Pending'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </AppLayout>
  );
}